import { cn } from '@gozoki/ui/lib/utils'
import { useEffect, useRef, useState } from 'react'
import { type VideoRTCStateDetail, VideoRTC } from './video-rtc'

if (typeof window !== 'undefined' && !customElements.get('video-rtc')) {
  customElements.define('video-rtc', VideoRTC)
}

type PlayerStatus = 'connecting' | 'connected' | 'error'

interface Session {
  baseUrl: string
  token: string
}

// Player go2rtc (WebRTC + MSE fallback), porté du back-office sans dépendances UI externes.
export function StoreCameraPlayer({
  cameraName,
  session,
  retryNonce,
  className,
}: {
  cameraName: string
  session: Session
  retryNonce: number
  className?: string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<PlayerStatus>('connecting')

  const sessionRef = useRef(session)
  useEffect(() => {
    sessionRef.current = session
  }, [session])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const s = sessionRef.current
    setStatus('connecting')

    const player = document.createElement('video-rtc') as VideoRTC
    player.filterPrivateCandidates = true
    player.resolveHostnameCandidates = true
    player.mode = 'webrtc,mse'

    const handleVideoPlaying = () => setStatus('connected')

    const handleStateChange = (event: Event) => {
      const { detail } = event as CustomEvent<VideoRTCStateDetail>
      if (detail.status === 'connected') {
        setStatus('connected')
      } else if (detail.status === 'error') {
        const video = player.video
        const fallbackActive =
          !!video &&
          (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ||
            video.networkState === HTMLMediaElement.NETWORK_LOADING)
        if (fallbackActive) return
        setStatus('error')
      }
    }

    player.addEventListener('videortcstatechange', handleStateChange)
    container.appendChild(player)
    player.style.cssText = 'display:block;width:100%;height:100%;'

    if (player.video) {
      player.video.controls = false
      player.video.muted = true
      player.video.className = 'w-full h-full object-contain'
      player.video.addEventListener('playing', handleVideoPlaying)
    }

    const wsUrl = new URL('/api/ws', s.baseUrl)
    wsUrl.searchParams.set('src', cameraName)
    wsUrl.searchParams.set('token', s.token)
    player.src = wsUrl.toString()

    return () => {
      player.removeEventListener('videortcstatechange', handleStateChange)
      player.video?.removeEventListener('playing', handleVideoPlaying)
      player.dispose()
      if (container.contains(player)) {
        container.removeChild(player)
      }
    }
  }, [cameraName, retryNonce])

  return (
    <div className={cn('relative overflow-hidden rounded-2xl bg-black', className)}>
      <div ref={containerRef} className="absolute inset-0" />
      {status !== 'connected' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-center text-xs text-white">
          {status === 'connecting' ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Connexion…
            </>
          ) : (
            <span>Flux indisponible</span>
          )}
        </div>
      )}
    </div>
  )
}
